import React, { useEffect, useRef } from "react";

import { Grid, LoadMoreAnchor, LoadingMore } from "./styles";

interface MenuItemsGridProps {
    hasMore: boolean;
    loadingMore: boolean;
    loadedCount: number;
    onLoadMore: () => void;
    children: React.ReactNode;
}

/*
* Сетка плиток с догрузкой по достижении конца списка. IntersectionObserver, а не кнопка
* «Показать ещё»: на меню в 800 позиций пользователь иначе кликал бы двадцать раз.
* */
export const MenuItemsGrid = ({
    hasMore,
    loadingMore,
    loadedCount,
    onLoadMore,
    children,
}: MenuItemsGridProps) => {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    // Колбэк в ref: он пересоздаётся на каждый ответ, а наблюдатель должен пережить это
    // без переподписки — иначе на границе экрана запускается лишний запрос.
    const loadMoreRef = useRef(onLoadMore);

    useEffect(() => {
        loadMoreRef.current = onLoadMore;
    }, [onLoadMore]);

    useEffect(() => {
        const anchor = anchorRef.current;
        if (!anchor || !hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    loadMoreRef.current();
                }
            },
            { rootMargin: "300px" }
        );

        observer.observe(anchor);

        return () => observer.disconnect();
        // loadedCount в зависимостях обязателен: IntersectionObserver сообщает о смене
        // видимости, а не о том, что элемент всё ещё виден. Без переподписки после
        // догрузки якорь, оставшийся в зоне видимости, больше не сработал бы и список
        // замер бы на второй странице.
    }, [hasMore, loadedCount]);

    return (
        <>
            <Grid>{children}</Grid>
            {hasMore && <LoadMoreAnchor ref={anchorRef} />}
            {loadingMore && <LoadingMore>Загружаем ещё…</LoadingMore>}
        </>
    );
};
